<template>
  <div class="flex items-center h-full w-full relative">
    <span :class="cellClasses">{{ displayValue }}</span>
    
    <!-- Loading indicator when autocomplete is fetching -->
    <!-- <div v-if="isAutocompleteLoading" class="absolute top-0.5 right-1 text-primary animate-spin text-xs pointer-events-none">
      
    </div> -->
    
    <!-- API data indicator -->
    <!-- <div v-else-if="hasApiData" class="absolute top-0.5 right-0.5 text-primary/70 hover:text-primary text-xs pointer-events-none">
      
    </div> -->
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  params: any
}

const props = defineProps<Props>()

// Add error handling for params access
const safeParams = computed(() => props.params || {})

// Track autocomplete loading state from AG Grid context
const isAutocompleteLoading = computed(() => {
  try {
    const context = safeParams.value.context
    const loadingCells = context?.autocompleteLoadingCells
    
    if (loadingCells && cellId.value) {
      return loadingCells.get(cellId.value) || false
    }
    
    return false
  } catch (error) {
    console.warn('BotanicalNameCell: Error checking loading state:', error)
    return false
  }
})

// Get unique identifier for this cell using AG Grid's reliable methods
const cellId = computed(() => {
  try {
    const params = safeParams.value
    
    // Try multiple methods to get a reliable row identifier
    let rowIdentifier = null
    
    // Method 1: Use rowIndex if available
    if (params.rowIndex !== undefined && params.rowIndex !== null) {
      rowIdentifier = params.rowIndex
    }
    // Method 2: Use node.rowIndex as fallback
    else if (params.node?.rowIndex !== undefined && params.node?.rowIndex !== null) {
      rowIdentifier = params.node.rowIndex
    }
    // Method 3: Use node.id as last resort
    else if (params.node?.id !== undefined && params.node?.id !== null) {
      rowIdentifier = `node_${params.node.id}`
    }
    // Method 4: Generate from node reference
    else {
      rowIdentifier = 'fallback'
    }
    
    const colId = params.colDef?.field || 'unknown'
    return `${rowIdentifier}-${colId}`
  } catch (error) {
    console.warn('BotanicalNameCell: Error generating cellId:', error)
    return 'unknown'
  }
})

const displayValue = computed(() => {
  try {
    const value = safeParams.value.value
    if (value === null || value === undefined) return ''
    
    if (typeof value === 'object' && value !== null) {
      return value.label || value.value || ''
    }
    
    return value
  } catch (error) {
    console.warn('BotanicalNameCell displayValue error:', error)
    return ''
  }
})

const hasApiData = computed(() => {
  try {
    const value = safeParams.value.value
    return typeof value === 'object' && value !== null && 
      (value.sourceName || value.groundingMetadata || value.sourceText)
  } catch (error) {
    console.warn('BotanicalNameCell hasApiData error:', error)
    return false
  }
})

const cellClasses = computed(() => {
  const baseClasses = 'font-medium text-sm leading-normal'
  
  if (hasApiData.value) {
    return `${baseClasses} border-l-2 border-primary/30 pl-2`
  }
  
  return baseClasses
})
</script> 